import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrentProductService } from '@spartacus/storefront';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { SdPushSliderComponent } from '@seb/web-design-system/push-slider';
import {
  BrandsEnum, DeviceService, IComponentWrapper,
  IExtendedLink,
  ILinkMedia,
  IPushCardWithLayout,
  PositionEnum, SdComponentWrapperComponent,
  SizesEnum
} from '@seb/web-design-system/core';
import {
  LameConfig, ProductBenefit, PushCardComponentInterface,
  PushCardImageRatioEnum,
  PushCardMediaConfigEnum,
  PushCardSizeEnum,
  PushCardTextConfigEnum
} from '@seb/web-common/core/models';
import { UppercaseThemesEnum } from '@seb/web-common/core/enums';
import { BrandCornerMapper, PushCardMapper } from '@seb/web-common/core/mappers';
import { ConfigurationService, TrackingService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-benefits-card-push',
  templateUrl: './benefits-card-push.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdPushSliderComponent
  ]
})
export class BenefitsCardPushComponent implements OnInit, OnDestroy {
  pushs: IPushCardWithLayout[];
  hasReadMore = true;
  show: boolean;
  titleLevel = 2;
  size = SizesEnum.s;
  wrapper: IComponentWrapper;
  textConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center = PositionEnum.center;
  lameConfig: LameConfig;
  private isMobile$ = false;
  private mobileSubscription: Subscription;

  @Input()
  isProductBenefits = false;

  @Input() pageTemplate = '';

  @Input() brand = BrandsEnum.default;

  private readonly destroy$: Subject<void> = new Subject();
  constructor(
    private currentProductService: CurrentProductService,
    private pushCardMapper: PushCardMapper,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private configService: ConfigurationService,
    private cdRef: ChangeDetectorRef,
    private brandCornerMapper: BrandCornerMapper,
    private deviceService: DeviceService
  ) {}

  ngOnInit(): void {
    this.mobileSubscription = this.deviceService.isMobile.pipe(takeUntil(this.destroy$)).subscribe((isMobile: boolean) => {
      this.isMobile$ = isMobile;
      this.cdRef.markForCheck();
    });
    const config = this.configService.config;
    const product$ = this.currentProductService.getProduct();
    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.benefits;

    product$
      .pipe(
        filter((p) => !!p),
        takeUntil(this.destroy$)
      )
      .subscribe((product) => {
        this.size = (config?.components?.pushSlider?.sizeForce as SizesEnum) || this.size;
        this.pushs = product?.productBenefits
          ? product.productBenefits
            .filter(
              (productBenefit) => !!productBenefit.image && !!productBenefit.description && !!productBenefit.title
            )
            .map((productBenefit) => this.productToIPushCardWithLayout(productBenefit))
          : [];
        if (this.isProductBenefits) {
          this.hasReadMore = !this.isMobile$;
          this.size = SizesEnum.s;
          this.show = config.components?.benefits?.show;
          this.wrapper = this.toWrapper(product.name);
        }
        this.cdRef.markForCheck();
      });
  }

  onClickLink(event: IExtendedLink | ILinkMedia): void {
    this.trackingService.sendEvent('clickBenefitsCardPushLink', {
      appType: event.linkName
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mobileSubscription?.unsubscribe();
  }

  private productToIPushCardWithLayout(productBenefit: ProductBenefit): IPushCardWithLayout {
    const pushCardComponentInterface: PushCardComponentInterface = {
      headline: productBenefit.title,
      description: productBenefit.description,
      media: productBenefit.image,
      imageRatio: this.isProductBenefits ? PushCardImageRatioEnum.R1_1 : PushCardImageRatioEnum.R4_3,
      mediaConfig: PushCardMediaConfigEnum.TOP,
      textConfig: this.isProductBenefits ? PushCardTextConfigEnum.LEFT : PushCardTextConfigEnum.CENTER,
      theme: UppercaseThemesEnum.LIGHT,
      pushSize: PushCardSizeEnum.S,
      isDescription: 'false'
    };

    return this.pushCardMapper.toPushCardWithLayout(pushCardComponentInterface);
  }

  private toWrapper(productName: string): IComponentWrapper {
    return {
      heading: {
        size: SizesEnum.l,
        data: {
          title: this.translateService.instant('product.benefitsTitle', {
            productTitle: productName
          })
        },
        isUnderlined: !!this.lameConfig?.titleUnderlined,
        titleThemesConfig: this.lameConfig?.titleTheme,
        titleLevel: 2
      },
      footNotes: [],
      links: []
    };
  }
}
